import { Box, Typography, Tooltip, Button } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { CircleCheck, Loader } from "lucide-mui";
import { useTheme } from "../context/ThemeContext";

const CircularProgress = ({ progress, isDark }) => {
  const size = 18;
  const r = 7;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, progress)) / 100) * circ;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: "rotate(-90deg)", flexShrink: 0 }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={isDark ? "#393E44" : "#D5D9DF"}
        strokeWidth={2.5}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill='none'
        stroke={isDark ? "#41D179" : "#146E37"}
        strokeWidth={2.5}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap='round'
      />
    </svg>
  );
};

export default function FileUploadStatusComponent({
  fileName,
  status = "INGESTION_IN_PROGRESS",
  message = null,
  progress = null,
  onCancel,
}) {
  const { isDark } = useTheme();
  const isFailed = status === "INGESTION_FAILED";
  const isCompleted = status === "INGESTION_COMPLETED";
  const isCancelling = status === "CANCELING" || status === "INGESTION_CANCELING";
  const isCancelled = status === "FILE_DELETED";

  const displayMessage = message
    ? message.split(": ").slice(1).join(": ") || message
    : isCompleted
      ? "Complete"
      : isFailed
        ? "Upload Failed"
        : isCancelling
          ? "Canceling..."
          : isCancelled
            ? "Canceled"
            : "Uploading...";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: "12px 16px",
        backgroundColor: isDark ? "#171819" : "#F3F7FB",
        border: "1px solid",
        borderColor: isDark ? "#393E44" : "#D5D9DF",
        borderRadius: "12px",
        width: "260px",
        ml: "auto",
        mr: 0,
      }}
    >
      <Tooltip title={fileName} arrow>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "0.15%",
            color: isDark ? "#F8F9FACC" : "#434950",
            fontFamily: "Inter",
            mb: "8px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "fit-content",
            maxWidth: "100%",
          }}
        >
          {fileName}
        </Typography>
      </Tooltip>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {isCompleted ? (
          <CircleCheck
            sx={{ fontSize: 20, color: isDark ? "#41D179" : "#146E37" }}
          />
        ) : isFailed || isCancelled ? (
          <ErrorIcon sx={{ fontSize: 16, color: "#C12E30" }} />
        ) : progress != null ? (
          <CircularProgress progress={progress} isDark={isDark} />
        ) : (
          <Loader
            thickness={7}
            sx={{
              fontSize: 18,
              color: isCancelling ? "#C12E30" : isDark ? "#8C8C8C" : "#6A7079",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <Tooltip title={displayMessage} arrow>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: "Inter",
                lineHeight: "22px",
                color:
                  isFailed || isCancelling || isCancelled
                    ? "#C12E30"
                    : isDark
                      ? "#A8AEB6"
                      : "#6A7079",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {displayMessage}
            </Typography>
          </Tooltip>
        </Box>
        {onCancel && (
          <Button
            onClick={onCancel}
            size='small'
            disableRipple
            sx={{
              flexShrink: 0,
              fontFamily: "Inter",
              fontSize: "12px",
              fontWeight: 500,
              letterSpacing: "0.018px",
              lineHeight: "14px",
              px: "8px",
              height: "22px",
              minWidth: 0,
              borderRadius: "8px",
              border: "1px solid",
              borderColor: isDark ? "rgba(106, 112, 121, 0.5)" : "#D5D9DF",
              background: isDark
                ? "linear-gradient(158.55deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 100%)"
                : "linear-gradient(169.62deg, #F2F5FA 8.49%, #E9EBED 91.51%)",
              color: isDark ? "#ECEFF3" : "#31363B",
              textTransform: "none",
              "&:hover": {
                background: isDark
                  ? "linear-gradient(158.55deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 100%)"
                  : "linear-gradient(169.62deg, #F2F5FA 8.49%, #E9EBED 91.51%)",
              },
            }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
